/**
 * THE RETIRED PASSWORD ROUTES (passwordless-sign-in D12 "Retired routes (E9)",
 * th-1oyb0.9; spec `sign-in-door` § "Retired password routes land on the door
 * without reading their tokens").
 *
 * Tower Hunt signs in with an emailed code or a passkey, so the three pages a
 * password needed are gone: the reset request (`/forgot-password`), the reset
 * form (`/password-resets/:token`) and the emailed verification link
 * (`/email-verifications/:token`). The addresses are still in old emails. A
 * link that 404s, or that lands with no word of why, is what the spec rules
 * out: each one lands on the sign-in door (the workspace, for a member) with a
 * notice saying the old links no longer work.
 *
 * ── THE TOKEN IS NEVER READ ─────────────────────────────────────────────────
 * The `:token` segment is a credential from a retired flow. The guard below
 * takes no argument, so it cannot read the route, its params or its query:
 * the token is never sent anywhere, stored, logged or carried into the landing
 * URL. The segment stays in the paths only so an old link still MATCHES a
 * record rather than falling through to the unmatched-path arm.
 *
 * ── THE NOTICE RIDES THE NOTIFICATIONS STORE, NEVER THE QUERY ──────────────
 * A query-borne notice would not survive the member's landing:
 * `signedInLanding` drops every param a guest surface carries. So the guard
 * raises the toast itself (`legacyAuthLink` in the notifications manifest), and
 * App.vue's overlay shows it on whichever page the navigation lands.
 *
 * ── WHY IT RETURNS THE DOOR, AND NEVER DECIDES GUEST VS MEMBER ─────────────
 * "The door for a guest, the workspace for a member" is already one decision
 * with one owner: the global guard's guest-only arm (`router/index.js`), which
 * lets a guest onto `/sign-in` and sends a member to `signedInLanding`. So the
 * guard here returns the door, and that arm decides, on the RESOLVED identity,
 * exactly as it does for every other arrival at the door. A second
 * guest-or-member test here would be a second owner of both the decision and
 * the member's landing. `retiredAdminRoutes.js` defers to the same global
 * guard for the same reason.
 *
 * That is why the records declare NO meta. The global guard runs BEFORE a
 * record's `beforeEnter`:
 *   - `guestOnly` would send a member to the workspace before the notice is
 *     raised;
 *   - `requiresAuth` would send a guest to sign-in with the old path, token
 *     and all, in `?redirect=`.
 *
 * ── WHY THE RECORDS CARRY NO NAME ───────────────────────────────────────────
 * Nothing pushes these destinations. Their views are unrouted and deleted with
 * the password flow (th-1oyb0.12). The records exist for the PATHS in old
 * emails only, as the retired `/admin` records do.
 */

import { useNotificationsStore } from "@/stores/notifications";

/** The notifications-manifest kind every retired path raises. */
export const LEGACY_AUTH_LINK_NOTICE = "legacyAuthLink";

// Long enough to read a two-sentence notice on a page the reader did not
// expect to land on.
const NOTICE_SECONDS = 8;

/** The retired paths, in the order the old flow used them. */
export const RETIRED_AUTH_PATHS = Object.freeze([
  "/forgot-password",
  "/password-resets/:token",
  "/email-verifications/:token",
]);

/**
 * Raise the legacy-link notice and send the navigation to the door. The global
 * guard's guest-only arm then admits a guest there and lands a member on the
 * workspace. Takes nothing, so it reads nothing of the route it retires.
 *
 * @returns {{ name: string }} the door's route.
 */
export function landOnTheDoor() {
  useNotificationsStore().addNotification(LEGACY_AUTH_LINK_NOTICE, NOTICE_SECONDS);

  return { name: "SignIn" };
}

/**
 * The three route records, built from the one list.
 *
 * @param {object|Function} placeholderComponent — never rendered; the guard
 *   always redirects. A record with neither component nor children warns at
 *   build.
 * @returns {Array<object>} vue-router route records.
 */
export function retiredAuthRouteRecords(placeholderComponent) {
  return RETIRED_AUTH_PATHS.map((path) => ({
    path,
    beforeEnter: () => landOnTheDoor(),
    component: placeholderComponent,
  }));
}
